/**
 * Biomarker categorization — the single source of truth for mapping any biomarker
 * to one of the 9 functional systems (or "other").
 *
 * HYBRID: STATIC MAP (reviewable) + RULES (fallback). A reviewed static map
 * (`biomarker-category-map.ts`, seeded from these rules) makes the category of the
 * known LOINC universe auditable and durable — a clinician can correct an entry and
 * a later rule tweak won't silently override it. The rule engine remains the fallback
 * for the long tail: LOINC has many sibling codes per analyte and practices keep
 * ordering new panels, so a never-before-seen "Cholesterol in LDL ..." variant absent
 * from the map still categorizes as metabolism the instant it appears — no maintenance.
 *
 * Resolution precedence (see getCategoryForLoinc):
 *   1. BIOMARKER_CONFIG[base].category   — curated rich config, authoritative
 *   2. CATEGORY_OVERRIDES[base]          — small hand-curated corrections
 *   3. STATIC_CATEGORY_MAP[base]         — reviewed listing (wins over rules; durable to rule edits)
 *   4. categorizeByRules(displayName)    — the engine (covers the long tail + future codes)
 *   5. "other"                           — genuine residue (incl. excluded panels)
 *
 * REVIEWABILITY: a golden-snapshot test (apps/web) runs categorizeByRules over the
 * bundled top-4000 LOINC names, so every rule change surfaces as a reviewable diff and
 * greedy-substring regressions are caught in CI.
 *
 * FAILURE MODE IS BENIGN: a wrong category only mis-places a biomarker under a filter
 * tab — no reference ranges, no data-integrity or safety impact. That tolerance is why
 * a runtime rule engine (vs an exhaustively hand-verified map) is the right trade.
 *
 * EXCLUSIONS BY DESIGN: food-sensitivity / allergen / stool-microbe / genetic tests must
 * NOT land in a functional system. They naturally fall through to "other" (no rule
 * matches), and SKIP patterns guard against a greedy rule ever grabbing them.
 */

import { getStaticCategoryForLoinc } from "./biomarker-category-map";
import { BIOMARKER_CONFIG } from "./biomarker-config";
import type { FunctionalSystem } from "./types";

/** The 9 real systems — everything the rule engine can assign (never "other"). */
type CategorizableSystem = Exclude<FunctionalSystem, "other">;

// -----------------------------------------------------------------------------
// Skip patterns — force "other" even if a rule below would match. These are the
// panel classes that must never be categorized (see EXCLUSIONS BY DESIGN above).
// -----------------------------------------------------------------------------
const SKIP: readonly RegExp[] = [
  /triggered leukotriene release/i, // food/allergen basophil-activation functional tests
  /\bIgE\b/i, // specific-IgE = allergen sensitivity (food/environmental), not trendable
  // Food-sensitivity IgG/IgA antibody panels named after a mineral/nutrient ("Copper IgG Ab",
  // "Zinc IgG Ab") otherwise hit the minerals rule and leak into Nutrients. Scope the skip to the
  // mineral-token + antibody-class co-occurrence so real serum minerals ("Copper, Serum") and real
  // infectious IgG serology ("EBV IgG Ab" — no mineral token) are untouched.
  /\b(?:calcium|iron|zinc|magnesium|copper|selenium|manganese|chromium|molybdenum|iodine)\b.*\bIg[GA]\b|\bIg[GA]\b.*\b(?:calcium|iron|zinc|magnesium|copper|selenium|manganese|chromium|molybdenum|iodine)\b/i,
  /gene.*(mutation|variant)|\[Identifier\]|genotype|\ballele\b/i, // genetic tests
  /\[HIV-SSC\]/i, // HIV symptom-severity checklist items
  /minimum data set|questionnaire|serving size/i,

  // No functional-system home → "other" (mirrors BIOMARKER_CONFIG, which parks these too). These
  // are real analytes, but none of the nine systems fits; forced into `other` for consistency across
  // the config + map + rule layers, pending a future `renal` / `exposure` category. Keep these ABOVE
  // the rule table so they win over a greedier token below (e.g. urine `albumin` → nutrients).
  //
  // Kidney function: no `renal` category. `\bcreatinine\b` also sweeps the ratios (BUN/Creatinine,
  // Albumin/Creatinine, Protein/Creatinine); it never matches muscle "creatine kinase" (different
  // word). The urine co-occurrence guards force urine albumin/protein to `other` while SERUM albumin
  // stays a nutrient.
  /\bcreatinine\b|urea nitrogen|\bBUN\b|\beGFR\b|glomerular filtration|\bcystatin\b|microalbumin/i,
  /\burine\b.{0,40}\b(?:albumin|protein)\b|\b(?:albumin|protein)\b.{0,40}\burine\b/i,
  // Toxic heavy metals: exposure/toxin load, not a nutrient trace mineral. Word-bounded so it can't
  // hit unrelated tokens; `lead` is intentionally omitted (too many false positives — "lead time",
  // ECG "lead") and already falls through to `other` since no rule matches it.
  /\barsenic\b|\bmercury\b|\bcadmium\b|\bthallium\b|\bantimony\b/i,
  // Vitamin D — hormone AND fat-soluble vitamin, fits neither cleanly. Must SKIP (not just drop its
  // rule): vitamin-D-with-calcium-challenge names ("25-hydroxyvitamin D3 ... post dose calcium")
  // otherwise hit the minerals rule and leak into Nutrients.
  /vitamin d\b|25-hydroxyvitamin|dihydroxyvitamin|calcidiol|calcitriol|cholecalciferol|ergocalciferol/i,
  // Infectious-disease serology matches no remaining rule (its terms were removed from the immune
  // rule below), so it already resolves to `other`; no SKIP needed.
];

// -----------------------------------------------------------------------------
// Ordered rule table (specific -> general). First match wins. Matched against the
// biomarker's display name / LOINC long-common-name. Conventions mirror the curated
// BIOMARKER_CONFIG (lipids/glucose -> metabolism; liver/pancreatic -> digestive; WBC differential +
// acute-phase -> inflammation; RBC indices + minerals + vitamins + proteins -> nutrients; hormones ->
// endocrine; bone + muscle enzymes -> musculoskeletal; troponin/BNP -> cardiovascular). Diagnostics
// with no functional-system home (renal chemistry, toxic metals, Vitamin D, infectious serology,
// autoantibodies, immune-cell subsets) are SKIP-listed or unmatched -> `other`.
// -----------------------------------------------------------------------------
const RULES: readonly { re: RegExp; category: CategorizableSystem }[] = [
  // specific pre-rules that must beat greedier general tokens below
  { re: /ascorbate|ascorbic acid/i, category: "nutrients" }, // vitamin C; beats "in Leukocytes"
  { re: /\bbutyrate\b|propionate|short.chain fatty|\bSCFA\b/i, category: "digestive" }, // gut SCFA; beats complement "C4"
  { re: /pyridoxal|pyridoxine/i, category: "nutrients" }, // vitamin B6; beats "phosphate" in "Pyridoxal phosphate"
  { re: /creatine kinase\.?\s?mb|\bCK[\s-]?MB\b/i, category: "cardiovascular" }, // cardiac isoenzyme; beats generic CK (musculoskeletal)
  { re: /potassium.*(?:RBC|red blood cell|erythrocyte)/i, category: "nutrients" }, // intracellular K+ = nutrient status; beats the serum-electrolyte potassium rule (metabolism)

  // endocrine: thyroid, sex/adrenal hormones (vitamin D is deliberately excluded — see below)
  {
    // `\bT4\b(?!\s*helper)` matches thyroxine T4 but NOT "T4 helper" (a CD4 immune-subset synonym),
    // which must route to `other` with the rest of the CD3/CD4/CD8/NK diagnostics below.
    re: /thyro(id|peroxidase|globulin)|\bTSH\b|thyrotropin|\bT3\b|\bT4\b(?!\s*helper)|triiodothyronine|thyroxine/i,
    category: "endocrine",
  },
  {
    re: /testosterone|estradiol|estrone|estriol|\bestrogen|progesterone|\bDHEA|androstene|pregnenolone|cortisol|cortisone|aldosterone|\bFSH\b|follicle-stimulating|luteinizing|lutropin|follitropin|\bLH\b|prolactin|sex hormone|\bSHBG\b|anti-mullerian|\bAMH\b|insulin-like growth factor|somatomedin|\bIGF|growth hormone|calcitonin/i,
    category: "endocrine",
  },
  // Vitamin D (25-hydroxy / calcidiol / calcitriol / cholecalciferol / ergocalciferol) is deliberately
  // NOT categorized: it's both a hormone and a fat-soluble vitamin, fits neither `endocrine` nor
  // `nutrients` cleanly, so it resolves to `other` (matching BIOMARKER_CONFIG). The nutrients vitamin
  // rule below already excludes it via `vitamin (?!d)`, and no other rule matches these tokens.
  { re: /\binsulin\b|c[\s-]?peptide|leptin|adiponectin|ghrelin/i, category: "metabolism" },

  // cardiovascular: the lipid panel (LDL/HDL/VLDL/cholesterol/triglycerides/apolipoproteins/Lp(a)).
  // Grouped with the heart under "Heart & Lipids" — these are cardiovascular-risk markers, not
  // metabolic chemistry. (Keep above the metabolism block so a lipid name doesn't fall through.)
  //
  // Token guards keep non-lipid-panel assays out: the bare `lipid` token is word-bounded so it
  // can't match `phospholipid` (lupus-anticoagulant aPTT/dRVVT confirms, antiphospholipid Ab
  // panels, phospholipid phosphorus), and `(?!\s+droplets?)` skips urine "lipid droplets". The
  // `cholesterol` token excludes the `cholesterol esterase` enzyme while keeping `cholesterol
  // esters`.
  {
    re: /cholesterol(?!\s+esterase\b)|lipoprotein|\bLDL\b|\bHDL\b|\bVLDL\b|triglyceride|apolipoprotein|\bapo ?b\b|\bapo ?a\b|\bLp\(a\)|\bLp-?PLA|non-hdl|\blipids?\b(?!\s+droplets?)/i,
    category: "cardiovascular",
  },
  // metabolism: fatty acids, glucose, renal chemistry, electrolytes, body comp
  {
    // `\bomega\b` (word-bounded) so it matches "Omega-3 fatty acids" but NOT the "omega" buried in
    // "cytomegalovirus" — an unbounded /omega/ mis-routed CMV serology into metabolism.
    re: /fatty acid|\bomega\b|\bEPA\b|\bDHA\b|\bDPA\b|arachidonic|linoleic|linolenic|oleic|palmitic|stearic|myristic/i,
    category: "metabolism",
  },
  {
    // `\blactate\b(?!...dehydrogenase)` so "Lactate dehydrogenase"/LDH falls through to the
    // musculoskeletal muscle-enzyme rule instead of matching bare "lactate" here (same analyte, one tab).
    re: /glucose|hemoglobin a1c|\bhba1c\b|\ba1c\b|fructosamine|glycated|glycohemoglobin|ketone|beta-hydroxybutyrate|\blactate\b(?!\s+dehydrogenase)|pyruvate/i,
    category: "metabolism",
  },
  {
    // Uric acid / urate only — purine metabolism (gout). Kidney-function chemistry (creatinine, BUN,
    // eGFR, cystatin, urine albumin/protein) is forced to `other` by the SKIP block above, since
    // there is no `renal` category among the nine.
    re: /uric acid|urate/i,
    category: "metabolism",
  },
  {
    re: /\bsodium\b|\bpotassium\b|\bchloride\b|bicarbonate|carbon dioxide|\bCO2\b|anion gap|osmolality/i,
    category: "metabolism",
  },
  {
    re: /body mass index|\bBMI\b|body fat|body weight|body height|waist|visceral|basal metabolic|\bsteps?\b|calories|distance/i,
    category: "metabolism",
  },

  // digestive: liver + pancreatic enzymes, gut markers
  {
    re: /alanine aminotransferase|aspartate aminotransferase|\bALT\b|\bAST\b|\bSGPT\b|\bSGOT\b|bilirubin|gamma[\s-]glutamyl|\bGGT\b|\b5'-nucleotidase\b/i,
    category: "digestive",
  },
  {
    re: /amylase|lipase|elastase|calprotectin|lactoferrin|steatocrit|fecal fat|\bgastrin\b|\bH\. ?pylori|helicobacter|occult blood|zonulin|secretory iga/i,
    category: "digestive",
  },

  // musculoskeletal: bone turnover, muscle enzymes, fitness/mobility
  {
    re: /alkaline phosphatase|\bALP\b|osteocalcin|c-telopeptide|\bCTX\b|\bP1NP\b|procollagen|\bphosphorus\b|\bphosphate\b|parathyr|creatine kinase|\bCK\b|myoglobin|lactate dehydrogenase|\bLDH\b|aldolase|\bVO2|grip strength|training load|mobility/i,
    category: "muscular_skeletal",
  },

  // cardiovascular: cardiac injury + cardiac-risk markers
  { re: /\btroponin/i, category: "cardiovascular" },
  {
    re: /\bBNP\b|natriuretic|\bNT-proBNP\b|\bD-dimer\b|\bhomocysteine\b|myeloperoxidase|galectin|\bST2\b/i,
    category: "cardiovascular",
  },

  // inflammation: WBC differential + acute-phase markers only (autoantibodies, immune-cell subsets,
  // and infectious serology are diagnostics → they route to `other`; see the note below the rules)
  {
    re: /leukocyte|white blood cell|\bWBC\b|neutrophil|lymphocyte|\blymphs?\b|monocyte|eosinophil|basophil|granulocyte|\bplatelet|platelets|\bMPV\b|band form|myelocyte|metamyelocyte|promyelocyte/i,
    category: "inflammation",
  },
  {
    re: /c-reactive protein|\bCRP\b|sedimentation rate|\bESR\b|procalcitonin|interleukin|\bIL-\d|tumor necrosis|\bTNF|fibrinogen|serum amyloid|complement/i,
    category: "inflammation",
  },
  // Autoimmune autoantibodies (ANA / RF / anti-CCP / dsDNA / ENA / ANCA…), immune-cell subset counts
  // (CD4 / CD8 / CD3 / natural killer), and infectious-disease serology are disease DIAGNOSTICS, not
  // trendable functional-system markers — they match no rule and resolve to `other` (mirroring the
  // curated BIOMARKER_CONFIG). The WBC-differential + acute-phase rules above keep the routine CBC
  // white line + CRP / ESR / IL-6 in inflammation. (A generic "lymphocyte subset" still hits the WBC
  // rule via `lymphocyte`; only the named CD/NK counts fall through.)

  // nutrients: RBC indices, proteins, vitamins, minerals
  {
    re: /hematocrit|\bHCT\b|erythrocyte|red blood cell|\bRBC\b|hemoglobin|\bMCV\b|\bMCH\b|\bMCHC\b|\bRDW\b|reticulocyte/i,
    category: "nutrients",
  },
  {
    re: /total protein|\balbumin\b|\bglobulin\b|a\/g ratio|prealbumin|transthyretin/i,
    category: "nutrients",
  },
  {
    re: /vitamin (?!d)|\bfolate\b|folic acid|cobalamin|methylmalon|\bB12\b|\bB6\b|pyridox|thiamin|riboflavin|niacin|biotin|pantothen|ascorb|tocopherol|retinol|\bB1\b|\bB2\b|\bB3\b|\bB5\b|\bB9\b/i,
    category: "nutrients",
  },
  {
    re: /\bcalcium\b(?!\s+binding)|\biron\b|iron binding|\bTIBC\b|\bUIBC\b|transferrin|\bferritin\b|\bzinc\b|\bmagnesium\b|\bcopper\b|\bselenium\b|\bmanganese\b|\bchromium\b|\bmolybdenum\b|\biodine\b/i,
    category: "nutrients",
  },
];

// -----------------------------------------------------------------------------
// Hand-curated corrections. Keyed by BASE LOINC. Use this ONLY to override a
// specific code the rules get wrong (or leave uncategorized) — not to bulk-load a
// map. Keeping this small is the signal that the rules are doing their job.
// -----------------------------------------------------------------------------
export const CATEGORY_OVERRIDES: Readonly<Record<string, CategorizableSystem>> = {
  // (intentionally empty to start — add entries only as review surfaces rule misses)
};

/**
 * Categorize a biomarker by its display name using the rule engine.
 * Returns a functional system, or `undefined` if no rule matches (caller maps to "other").
 * Pure function of the name — deterministic and snapshot-testable.
 */
export function categorizeByRules(
  displayName: string | undefined,
): CategorizableSystem | undefined {
  if (!displayName?.length) return undefined;
  if (SKIP.some((re) => re.test(displayName))) return undefined;
  for (const rule of RULES) {
    if (rule.re.test(displayName)) return rule.category;
  }
  return undefined;
}

/**
 * Resolve a biomarker's functional-system category. This is the ONE place category
 * is derived — every call site (server discovery, web/mobile filters) must use it.
 *
 * @param loincCode - the BASE LOINC (callers strip unit-split / source-qualified keys first)
 * @param displayName - the biomarker's display name, used by the rule engine fallback
 */
export function getCategoryForLoinc({
  loincCode,
  displayName,
}: {
  loincCode: string;
  displayName?: string;
}): FunctionalSystem {
  return (
    BIOMARKER_CONFIG[loincCode]?.category ??
    CATEGORY_OVERRIDES[loincCode] ??
    getStaticCategoryForLoinc(loincCode) ??
    categorizeByRules(displayName) ??
    "other"
  );
}
